import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';
import posthog from '@/lib/posthog';

/**
 * ErrorBoundary - Catches JavaScript errors anywhere in the component tree
 * Note: Error boundaries must be class components in React
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);

    // Capture error to PostHog
    if (typeof window !== 'undefined' && posthog) {
      posthog.captureException(error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: 'ErrorBoundary',
        errorMessage: error.message,
        errorName: error.name
      });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-base-100">
          <div className="max-w-md w-full p-8 bg-base-100 rounded-lg shadow-lg">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-error/20 rounded-full">
              <AlertTriangle className="w-8 h-8 text-error" />
            </div>
            <h1 className="text-2xl font-bold text-center text-base-content mb-4">
              Oops! Something went wrong
            </h1>
            <p className="text-base-content/70 text-center mb-6">
              We're sorry for the inconvenience. An unexpected error has occurred.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="bg-base-200 p-4 rounded mb-6 text-xs overflow-auto max-h-40">
                {this.state.error.toString()}
              </pre>
            )}
            <div className="flex gap-4 justify-center">
              <Button onClick={this.handleReset} variant="primary">
                Try Again
              </Button>
              <Button onClick={() => window.location.href = '/'} variant="ghost">
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };